// src/views/WatchLabsView.js — extracted from App.js
import React, { useState, useRef, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { compressImage, detectWatchFromPhoto, aiProxy, fileToBase64 } from "../lib/utils";
import { SK } from "../constants";
import { useLanguage, t } from "../lib/i18n";

// ─── WATCH LABS VIEW ──────────────────────────────────────────────────────────
export function WatchLabsView({ session, openAuth, toast, watchDesigns, saveDesigns, designOrders, saveDesignOrders, addNotif, aiEnabled }) {
  const { lang } = useLanguage();
  const u = session?.user;
  const userRoles   = u ? [...new Set([...(u.roles||[]), u.role].filter(Boolean))] : [];
  const isAdmin     = userRoles.includes("admin");
  const isSeller    = isAdmin || userRoles.includes("seller");
  const isCollector = isAdmin || userRoles.includes("collector");
  const canSubmit   = isSeller || isCollector;

  const [tab, setTab] = useState("browse");

  const orderCountFor = (designId) =>
    (designOrders||[]).filter(o => o.designId === designId).reduce((a,o) => a + (o.quantity||1), 0);

  const visibleStatuses = new Set(["approved","funded","in_production","produced"]);
  const approvedDesigns = (watchDesigns||[]).filter(d => visibleStatuses.has(d.status));
  const myDesigns = u ? (watchDesigns||[]).filter(d => d.designerId === u.id) : [];
  const myOrders  = u ? (designOrders||[]).filter(o => o.userId === u.id) : [];

  const sideItems = [["browse", "Browse Designs", approvedDesigns.length]];
  if (u) sideItems.push(["my-orders", "My Pre-orders", myOrders.length]);
  if (canSubmit) {
    sideItems.push(["submit", "Submit a Design", null]);
    sideItems.push(["my-designs", "My Designs", myDesigns.length]);
  }

  return (
    <div className="fade-in">
      <div style={{marginBottom:20}}>
        <span className="sl">{t("labs_sl",lang)}</span>
        <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:32,letterSpacing:"-0.025em"}}>{t("labs_head",lang)}</h2>
        <p style={{color:"var(--ink2)",fontSize:14,marginTop:8,lineHeight:1.7}}>
          {t("labs_sub",lang)}
          {!canSubmit && u && <> {t("labs_sellers_only",lang)}</>}
        </p>
      </div>

      {/* Tab strip — horizontal scroll, no sidebar */}
      <div style={{display:"flex",borderBottom:"2px solid var(--bd)",marginBottom:24,overflowX:"auto",scrollbarWidth:"none",WebkitOverflowScrolling:"touch"}}>
        {sideItems.map(([t,label,count])=>(
          <button key={t} onClick={()=>setTab(t)}
            style={{flexShrink:0,padding:"10px 16px",border:"none",borderBottom:`2.5px solid ${tab===t?"var(--ink)":"transparent"}`,marginBottom:-2,background:"transparent",cursor:"pointer",fontSize:13,color:tab===t?"var(--ink)":"var(--ink2)",fontWeight:tab===t?600:400,fontFamily:"'DM Sans',sans-serif",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:6}}>
            {label}
            {count!=null && count>0 && <span style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)"}}>{count}</span>}
          </button>
        ))}
      </div>

      {/* Content */}
      <div>
        {tab==="browse"     && <LabsBrowse approvedDesigns={approvedDesigns} session={session} openAuth={openAuth} designOrders={designOrders} saveDesignOrders={saveDesignOrders} watchDesigns={watchDesigns} saveDesigns={saveDesigns} toast={toast} addNotif={addNotif} orderCountFor={orderCountFor}/>}
        {tab==="my-orders"  && u  && <LabsMyOrders myOrders={myOrders} watchDesigns={watchDesigns} orderCountFor={orderCountFor}/>}
        {tab==="my-orders"  && !u && <LabsRestricted msg="Sign in to see your pre-orders." onAction={openAuth} actionLabel="Sign in"/>}
        {tab==="submit"     && canSubmit  && <LabsSubmit session={session} watchDesigns={watchDesigns} saveDesigns={saveDesigns} toast={toast} addNotif={addNotif} aiEnabled={aiEnabled} isSeller={isSeller} isCollector={isCollector} onDone={()=>setTab("my-designs")}/>}
        {tab==="submit"     && !canSubmit && <LabsRestricted msg="Only verified sellers and collectors can submit designs."/>}
        {tab==="my-designs" && canSubmit  && <LabsMyDesigns myDesigns={myDesigns} orderCountFor={orderCountFor}/>}
      </div>
    </div>
  );
}


// ─── Labs sub-components ──────────────────────────────────────────────────────
export function LabsRestricted({ msg, onAction, actionLabel }) {
  return (
    <div className="card" style={{textAlign:"center",padding:"56px 24px"}}>
      <div style={{fontSize:36,marginBottom:14}}>🔒</div>
      <p style={{color:"var(--ink2)",fontSize:14,lineHeight:1.7,maxWidth:420,margin:"0 auto 16px"}}>{msg}</p>
      {onAction && <button className="btn-ink" onClick={onAction} style={{fontSize:13}}>{actionLabel} →</button>}
    </div>
  );
}


export function LabsBrowse({ approvedDesigns, session, openAuth, designOrders, saveDesignOrders, watchDesigns, saveDesigns, toast, addNotif, orderCountFor }) {
  const u = session?.user;
  const [placing, setPlacing] = useState(null);

  const myOrderFor = (d) => u ? (designOrders||[]).find(o => o.designId===d.id && o.userId===u.id) : null;

  const preOrder = async (d) => {
    if (!u) { openAuth(); return; }
    if (myOrderFor(d)) { toast("You've already pre-ordered this design."); return; }
    setPlacing(d.id);
    const order = {
      id: "do-"+Date.now()+Math.random().toString(36).slice(2,5),
      designId: d.id, userId: u.id, userName: u.name,
      quantity: 1, status: "interested",
      createdAt: new Date().toISOString(),
    };
    const nextOrders = [...(designOrders||[]), order];
    saveDesignOrders(nextOrders);
    const newCount = nextOrders.filter(o => o.designId===d.id).reduce((a,o)=>a+(o.quantity||1),0);
    const min = d.minOrders || 10;
    if (newCount >= min && d.status === "approved") {
      saveDesigns((watchDesigns||[]).map(x => x.id!==d.id ? x : {...x, status:"funded", fundedAt:new Date().toISOString()}));
      if (addNotif) {
        addNotif(d.designerId, {type:"approval", title:`Your design "${d.name}" is funded! 🎉`, body:`${newCount} pre-orders received — production can begin.`, link:{view:"labs"}});
        addNotif("admin",     {type:"approval", title:`Design funded — ${d.name}`, body:`${d.designerName} hit ${newCount} pre-orders. Coordinate production.`, link:{view:"labs"}});
      }
      toast(`🎉 Pre-ordered — design just hit ${min} and is now funded!`);
    } else {
      toast(`Pre-ordered. ${newCount}/${min} so far — share to push it over the line.`);
    }
    setPlacing(null);
  };

  if (approvedDesigns.length === 0) {
    return <div className="card" style={{textAlign:"center",padding:"56px 24px",color:"var(--ink3)",fontFamily:"'DM Mono',monospace",fontSize:12}}>No designs live yet. Be the first to submit one.</div>;
  }

  return (
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:16}}>
      {[...approvedDesigns].reverse().map(d => {
        const count = orderCountFor(d.id);
        const min = d.minOrders || 10;
        const pct = Math.min(100, Math.round((count/min)*100));
        const photoSrc = d.aiRenderUrl || (Array.isArray(d.photos)&&d.photos.length>0 ? (typeof d.photos[0]==="string"?d.photos[0]:d.photos[0]?.url) : null);
        const mine = myOrderFor(d);
        const isFunded = d.status === "funded" || d.status === "in_production" || d.status === "produced";
        return (
          <div key={d.id} className="card" style={{padding:0,overflow:"hidden",display:"flex",flexDirection:"column"}}>
            <div style={{height:180,background:"var(--bg2)",position:"relative"}}>
              {photoSrc
                ? <img src={photoSrc} alt={d.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                : <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100%",fontSize:36,color:"var(--ink3)"}}>⌚︎</div>}
              {d.aiRenderUrl && <span style={{position:"absolute",top:10,left:10,background:"rgba(17,17,24,.85)",color:"#fff",fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:"0.1em",padding:"3px 8px",borderRadius:4}}>AI RENDER</span>}
              {isFunded && <span className="tag tag-grn" style={{position:"absolute",top:10,right:10}}>{d.status==="produced"?"Produced":d.status==="in_production"?"In production":"Funded ✓"}</span>}
            </div>
            <div style={{padding:"16px 18px 18px",display:"flex",flexDirection:"column",flex:1}}>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.1em",marginBottom:4}}>{d.brand || "Custom"} · by {d.designerName}</div>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:19,marginBottom:6}}>{d.name}</div>
              {d.description && <p style={{fontSize:12.5,color:"var(--ink2)",lineHeight:1.55,margin:"0 0 12px",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{d.description}</p>}

              <div style={{marginTop:"auto"}}>
                {d.price>0 && <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,color:"var(--gold)",marginBottom:8}}>₹{d.price.toLocaleString("en-IN")}</div>}
                <div style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)",marginBottom:4}}>
                    <span>{count} / {min} PRE-ORDERS</span>
                    <span>{pct}%</span>
                  </div>
                  <div style={{height:6,background:"var(--bg3)",borderRadius:3,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${pct}%`,background:isFunded?"var(--grn)":"var(--ink)",transition:"width .3s"}}/>
                  </div>
                </div>
                {mine
                  ? <button disabled className="btn-ghost" style={{width:"100%",fontSize:12,color:"var(--grn)",borderColor:"var(--gnbd)",background:"var(--gnbg)",cursor:"default"}}>✓ You've pre-ordered</button>
                  : <button className="btn-ink" style={{width:"100%",fontSize:12,opacity:placing===d.id?0.6:1}} disabled={placing===d.id} onClick={()=>preOrder(d)}>{placing===d.id?"Pre-ordering…":"Pre-order →"}</button>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}


export function LabsMyOrders({ myOrders, watchDesigns, orderCountFor }) {
  if (myOrders.length === 0) {
    return <div className="card" style={{textAlign:"center",padding:"56px 24px",color:"var(--ink3)",fontFamily:"'DM Mono',monospace",fontSize:12}}>No pre-orders yet. Browse designs to back one.</div>;
  }
  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      {[...myOrders].reverse().map(o => {
        const d = (watchDesigns||[]).find(x => x.id===o.designId);
        if (!d) return null;
        const count = orderCountFor(d.id);
        const min = d.minOrders || 10;
        const photoSrc = d.aiRenderUrl || (Array.isArray(d.photos)&&d.photos.length>0 ? (typeof d.photos[0]==="string"?d.photos[0]:d.photos[0]?.url) : null);
        const isFunded = ["funded","in_production","produced"].includes(d.status);
        return (
          <div key={o.id} className="card" style={{display:"flex",gap:12,alignItems:"flex-start"}}>
            {photoSrc && <img src={photoSrc} alt="" style={{width:64,height:64,objectFit:"cover",borderRadius:8,border:"1.5px solid var(--bd)",flexShrink:0}}/>}
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.1em",marginBottom:3}}>{d.brand||"Custom"} · by {d.designerName}</div>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:16,marginBottom:5,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{d.name}</div>
              {d.price>0 && <div style={{fontFamily:"'Playfair Display',serif",fontSize:14,color:"var(--gold)",marginBottom:5}}>₹{d.price.toLocaleString("en-IN")}</div>}
              <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                <span className={`tag ${isFunded?"tag-grn":"tag-gold"}`} style={{fontSize:9}}>{isFunded?(d.status==="produced"?"Produced":d.status==="in_production"?"In production":"Funded ✓"):"Awaiting backers"}</span>
                <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)"}}>{count}/{min}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}


export function LabsMyDesigns({ myDesigns, orderCountFor }) {
  if (myDesigns.length === 0) {
    return <div className="card" style={{textAlign:"center",padding:"56px 24px",color:"var(--ink3)",fontFamily:"'DM Mono',monospace",fontSize:12}}>You haven't submitted any designs yet. Use the Submit tab to share your first one.</div>;
  }
  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      {[...myDesigns].reverse().map(d => {
        const count = orderCountFor(d.id);
        const min = d.minOrders || 10;
        const pct = Math.min(100, Math.round((count/min)*100));
        const photoSrc = d.aiRenderUrl || (Array.isArray(d.photos)&&d.photos.length>0 ? (typeof d.photos[0]==="string"?d.photos[0]:d.photos[0]?.url) : null);
        const statusTag = d.status==="approved" ? "tag-grn" : d.status==="funded"||d.status==="in_production"||d.status==="produced" ? "tag-grn" : d.status==="rejected" ? "tag-red" : "tag-gold";
        const statusLabel = d.status==="approved" ? "Live ✓" : d.status==="funded" ? "Funded 🎉" : d.status==="in_production" ? "In production" : d.status==="produced" ? "Produced" : d.status==="rejected" ? "Rejected" : "Pending review";
        return (
          <div key={d.id} className="card" style={{display:"flex",gap:12,alignItems:"flex-start"}}>
            {photoSrc && <img src={photoSrc} alt="" style={{width:72,height:72,objectFit:"cover",borderRadius:8,border:"1.5px solid var(--bd)",flexShrink:0}}/>}
            <div style={{flex:1,minWidth:0}}>
              <div style={{marginBottom:6}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:6,marginBottom:3}}>
                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.1em"}}>{d.brand||"Custom"} · {d.createdAt?.slice(0,10)}</div>
                  <span className={`tag ${statusTag}`} style={{flexShrink:0}}>{statusLabel}</span>
                </div>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:17,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{d.name}</div>
              </div>
              {d.description && <p style={{fontSize:12.5,color:"var(--ink2)",lineHeight:1.55,margin:"0 0 10px"}}>{d.description}</p>}
              {d.adminNote && d.status==="rejected" && (
                <div style={{background:"var(--rbg)",border:"1px solid var(--rbd)",borderRadius:8,padding:"8px 12px",marginBottom:8}}>
                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--red)",letterSpacing:"0.1em",marginBottom:3}}>ADMIN NOTE</div>
                  <p style={{fontSize:12,color:"var(--ink2)",margin:0}}>{d.adminNote}</p>
                </div>
              )}
              {d.status!=="pending" && d.status!=="rejected" && (
                <div>
                  <div style={{display:"flex",justifyContent:"space-between",fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)",marginBottom:4}}>
                    <span>{count} / {min} pre-orders</span>
                    <span>{pct}%</span>
                  </div>
                  <div style={{height:6,background:"var(--bg3)",borderRadius:3,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${pct}%`,background:pct>=100?"var(--grn)":"var(--ink)"}}/>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// Labs constants
const LABS_CASE_SHAPES = ["Round","Tonneau","Cushion","Rectangular","Square","Oval"];
const LABS_DIAL_COLOURS = ["Black","White","Champagne","Silver","Blue","Green","Salmon","Grey"];
const LABS_HANDS = ["Dauphine","Sword","Baton","Leaf","Syringe","Cathedral","Pencil"];
const LABS_INDICES = ["Applied baton","Arabic numerals","Roman numerals","Dot markers","Mixed"];
const LABS_STRAPS = ["Leather","Steel bracelet","NATO","Tropical rubber","Mesh","Suede"];


export function LabsSubmit({ session, watchDesigns, saveDesigns, toast, addNotif, aiEnabled, isSeller, isCollector, onDone }) {
  const [form, setForm]   = useState({ name:"", brand:"", description:"", price:"", minOrders:10 });
  const [photos, setPhotos] = useState([]);
  const [useAiSpec, setUseAiSpec] = useState(false);
  const [spec, setSpec]   = useState({ caseShape:"Round", caseDiameterMm:38, dialColour:"Black", hands:"Baton", indices:"Applied baton", strap:"Leather", complications:"" });
  const [aiRender, setAiRender] = useState(null); // {url, generating}
  const [submitting, setSubmitting] = useState(false);

  const designerRole = isSeller ? "seller" : isCollector ? "collector" : "member";
  const update = (k,v) => setForm(p => ({...p, [k]: v}));
  const updateSpec = (k,v) => setSpec(p => ({...p, [k]: v}));

  const addPhotos = (files) => {
    if (!files || files.length===0) return;
    const incoming = Array.from(files).slice(0, 6 - photos.length).map(f => ({ file:f, preview:URL.createObjectURL(f) }));
    setPhotos(prev => [...prev, ...incoming].slice(0, 6));
  };

  const generateRender = async () => {
    if (!aiEnabled) { toast("AI is currently disabled. Skip the AI render for now."); return; }
    setAiRender({ url:null, generating:true });
    try {
      const result = await aiProxy("renderDesign", {
        spec: { ...spec, brand: form.brand, name: form.name, description: form.description },
      });
      if (result?.imageUrl) setAiRender({ url: result.imageUrl, generating:false });
      else if (result?.base64) setAiRender({ url: `data:image/png;base64,${result.base64}`, generating:false });
      else { setAiRender(null); toast("AI render unavailable right now — submit without it for now."); }
    } catch (e) {
      console.warn("AI render failed", e);
      setAiRender(null);
      toast("Couldn't generate render — submit without it; admins can add one later.");
    }
  };

  const submit = async () => {
    if (!form.name.trim()) { toast("Give your design a name."); return; }
    if (!form.description.trim()) { toast("Add a short description so collectors know what they're backing."); return; }
    if (photos.length===0 && !aiRender?.url) { toast("Upload at least one reference photo or generate an AI render."); return; }

    setSubmitting(true);
    const did = "wd-" + Date.now();
    const photoUrls = [];
    for (let i=0; i<photos.length; i++) {
      try {
        const compressed = await compressImage(photos[i].file, 1600, 0.88);
        const path = `designs/${did}/${i}.jpg`;
        const { error } = await supabase.storage.from("watch-photos").upload(path, compressed, { upsert:true, contentType:"image/jpeg" });
        if (!error) {
          const { data:{publicUrl} } = supabase.storage.from("watch-photos").getPublicUrl(path);
          photoUrls.push(publicUrl);
        }
      } catch {}
    }

    let aiRenderUrl = null;
    if (aiRender?.url) {
      try {
        if (aiRender.url.startsWith("data:")) {
          const blob = await (await fetch(aiRender.url)).blob();
          const path = `designs/${did}/ai-render.png`;
          const { error } = await supabase.storage.from("watch-photos").upload(path, blob, { upsert:true, contentType:"image/png" });
          if (!error) {
            const { data:{publicUrl} } = supabase.storage.from("watch-photos").getPublicUrl(path);
            aiRenderUrl = publicUrl;
          }
        } else {
          aiRenderUrl = aiRender.url;
        }
      } catch {}
    }

    const newDesign = {
      id: did,
      designerId: session.user.id,
      designerName: session.user.name,
      designerRole,
      name: form.name.trim(),
      brand: form.brand.trim(),
      description: form.description.trim(),
      photos: photoUrls,
      aiRenderUrl,
      spec: useAiSpec ? spec : null,
      minOrders: Number(form.minOrders) || 10,
      price: form.price ? parseInt(form.price) : null,
      status: "pending",
      createdAt: new Date().toISOString(),
    };
    saveDesigns([...(watchDesigns||[]), newDesign]);
    if (addNotif) {
      addNotif("admin", {
        type:"request",
        title:`New Watch Labs design — ${newDesign.name}`,
        body:`${newDesign.designerName} submitted "${newDesign.name}" to Watch Labs. Approve it from Admin → Designs.`,
        link:{view:"admin", tab:"designs"},
      });
    }
    setSubmitting(false);
    setForm({ name:"", brand:"", description:"", price:"", minOrders:10 });
    setPhotos([]);
    setAiRender(null);
    setUseAiSpec(false);
    toast("Design submitted — pending admin review.");
    if (onDone) onDone();
  };

  return (
    <div className="card" style={{padding:"20px"}}>
      <div style={{marginBottom:16}}>
        <span className="sl">Submit a design</span>
        <p style={{fontSize:13,color:"var(--ink2)",lineHeight:1.6,margin:"6px 0 0"}}>
          Upload a sketch or reference photo. If 10 collectors pre-order, we'll produce it.
        </p>
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:12}}>
        <div>
          <label className="fl">Design name *</label>
          <input value={form.name} onChange={e=>update("name", e.target.value)} placeholder="e.g. HMT Kohinoor Reissue" type="text" autoComplete="off" autoCapitalize="words" required/>
        </div>
        <div>
          <label className="fl">Brand inspiration</label>
          <input value={form.brand} onChange={e=>update("brand", e.target.value)} placeholder="HMT / Allwyn / Original" type="text" autoComplete="off" autoCapitalize="words"/>
        </div>
        <div>
          <label className="fl">What is this watch? *</label>
          <textarea value={form.description} onChange={e=>update("description", e.target.value)} rows={3} placeholder="Describe the look, the story, and why collectors should back it." style={{resize:"vertical"}} autoCapitalize="sentences" required/>
        </div>
        <div>
          <label className="fl">Proposed unit price (₹)</label>
          <input type="number" inputMode="numeric" min="0" value={form.price} onChange={e=>update("price", e.target.value)} placeholder="e.g. 7500" autoComplete="off"/>
        </div>
        <div>
          <label className="fl">Minimum orders to produce</label>
          <input type="number" inputMode="numeric" min="1" value={form.minOrders} onChange={e=>update("minOrders", e.target.value)} placeholder="10" autoComplete="off"/>
        </div>
      </div>

      <div style={{marginBottom:18}}>
        <label className="fl">Reference photos (up to 6)</label>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {photos.map((p,i)=>(
            <div key={i} style={{position:"relative"}}>
              <img src={p.preview} alt="" style={{width:80,height:80,objectFit:"cover",borderRadius:8,border:"1.5px solid var(--bd)"}}/>
              <button onClick={()=>setPhotos(prev=>prev.filter((_,x)=>x!==i))} style={{position:"absolute",top:-6,right:-6,width:20,height:20,borderRadius:"50%",background:"#fff",border:"1px solid var(--bd)",fontSize:11,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>×</button>
            </div>
          ))}
          {photos.length < 6 && (
            <label style={{width:80,height:80,border:"1.5px dashed var(--bd)",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",background:"var(--bg2)",fontSize:24,color:"var(--ink3)"}}>
              +
              <input type="file" accept="image/*" multiple style={{display:"none"}} onChange={e=>addPhotos(e.target.files)}/>
            </label>
          )}
        </div>
      </div>

      <div style={{background:"var(--bg2)",borderRadius:12,padding:"14px 16px",marginBottom:18}}>
        <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
          <input type="checkbox" checked={useAiSpec} onChange={e=>setUseAiSpec(e.target.checked)} style={{width:16,height:16,margin:0}}/>
          <div>
            <div style={{fontSize:13,fontWeight:500}}>Add a spec sheet + AI render</div>
            <div style={{fontSize:11.5,color:"var(--ink3)",marginTop:2}}>Optional — pick case shape, dial, hands, etc. We'll generate a preview render with AI.</div>
          </div>
        </label>

        {useAiSpec && (
          <div style={{marginTop:14,paddingTop:14,borderTop:"1px solid var(--bd)"}}>
            <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:10}}>
              <div>
                <label className="fl">Case shape</label>
                <select value={spec.caseShape} onChange={e=>updateSpec("caseShape", e.target.value)}>
                  {LABS_CASE_SHAPES.map(s=><option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="fl">Case diameter (mm)</label>
                <input type="number" min="28" max="48" value={spec.caseDiameterMm} onChange={e=>updateSpec("caseDiameterMm", Number(e.target.value))}/>
              </div>
              <div>
                <label className="fl">Dial colour</label>
                <select value={spec.dialColour} onChange={e=>updateSpec("dialColour", e.target.value)}>
                  {LABS_DIAL_COLOURS.map(s=><option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="fl">Hands</label>
                <select value={spec.hands} onChange={e=>updateSpec("hands", e.target.value)}>
                  {LABS_HANDS.map(s=><option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="fl">Indices</label>
                <select value={spec.indices} onChange={e=>updateSpec("indices", e.target.value)}>
                  {LABS_INDICES.map(s=><option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="fl">Strap</label>
                <select value={spec.strap} onChange={e=>updateSpec("strap", e.target.value)}>
                  {LABS_STRAPS.map(s=><option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div style={{marginBottom:10}}>
              <label className="fl">Complications / other notes</label>
              <input value={spec.complications} onChange={e=>updateSpec("complications", e.target.value)} placeholder="e.g. date window at 3, sub-seconds at 6"/>
            </div>

            <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
              <button className="btn-gold" style={{fontSize:12,padding:"9px 18px",opacity:aiRender?.generating?0.6:1}} onClick={generateRender} disabled={aiRender?.generating}>
                {aiRender?.generating ? "Rendering…" : aiRender?.url ? "Regenerate render ↻" : "Generate AI render ✦"}
              </button>
              {aiRender?.url && <img src={aiRender.url} alt="AI render" style={{width:96,height:96,objectFit:"cover",borderRadius:8,border:"1.5px solid var(--bd)"}}/>}
              {!aiEnabled && <span style={{fontSize:11,color:"var(--ink3)",fontFamily:"'DM Mono',monospace"}}>AI currently disabled</span>}
            </div>
          </div>
        )}
      </div>

      <button className="btn-ink" style={{fontSize:14,padding:"13px 28px",width:"100%",opacity:submitting?0.6:1}} disabled={submitting} onClick={submit}>
        {submitting ? "Submitting…" : "Submit for review →"}
      </button>
      <p style={{fontSize:11,color:"var(--ink3)",fontFamily:"'DM Mono',monospace",textAlign:"center",marginTop:10,letterSpacing:"0.06em"}}>YOUR DESIGN WILL BE REVIEWED BY ADMIN BEFORE GOING LIVE</p>
    </div>
  );
}

// ─── Chat View ────────────────────────────────────────────────────────────────
